import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import whoopLogo from '@/assets/whoop-logo.svg';

interface WhoopConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConsent: () => void;
}

export default function WhoopConsentDialog({ open, onOpenChange, onConsent }: WhoopConsentDialogProps) {
  const [hasReadTerms, setHasReadTerms] = React.useState(false);
  const [consentGiven, setConsentGiven] = React.useState(false);

  const handleConsent = () => {
    if (hasReadTerms && consentGiven) {
      onConsent();
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <img src={whoopLogo} alt="WHOOP" className="h-6 object-contain" />
          </div>
          <AlertDialogTitle>Data Access Authorization</AlertDialogTitle>
          <AlertDialogDescription className="flex items-center gap-1 flex-wrap">
            Please review and consent to data access before connecting your <img src={whoopLogo} alt="WHOOP" className="h-3 inline-block mx-1" /> account.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ScrollArea className="h-[400px] w-full rounded-md border p-4">
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2">What Data We Access</h4>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li><strong>Profile:</strong> Basic account information</li>
                <li><strong>Recovery:</strong> Recovery scores and HRV (Heart Rate Variability)</li>
                <li><strong>Sleep:</strong> Sleep duration, quality, and sleep stages</li>
                <li><strong>Cycles:</strong> Daily strain and resting heart rate</li>
                <li><strong>Workouts:</strong> Exercise activities and performance metrics</li>
                <li><strong>Body Measurements:</strong> Weight, height, and other body metrics</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">How We Use Your Data</h4>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li>Auto-populate your daily check-ins for convenience</li>
                <li>Provide insights on mind-body connection and patterns</li>
                <li>Correlate physical recovery with mental wellness</li>
                <li>Display your biometric data in your dashboard</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Your Rights</h4>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li><strong>Access:</strong> View all your WHOOP data we've stored at any time</li>
                <li><strong>Delete:</strong> Remove all your WHOOP data from our system</li>
                <li><strong>Disconnect:</strong> Revoke access to your WHOOP account at any time</li>
                <li><strong>Control:</strong> Your data is never shared with third parties</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Data Security</h4>
              <p className="text-muted-foreground">
                All WHOOP data is encrypted in transit (HTTPS) and at rest. We comply with GDPR, CCPA, 
                and other data privacy regulations. We will never sell or lease your WHOOP data to third parties.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Data Retention</h4>
              <p className="text-muted-foreground">
                We only keep your WHOOP data while your account is connected. When you disconnect, 
                you can choose to delete all your WHOOP data from our system.
              </p>
            </div>

            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                By connecting, you acknowledge that Therappi complements (not competes with) WHOOP. 
                We enhance your wellness journey by connecting physical and mental health insights.
              </p>
            </div>
          </div>
        </ScrollArea>

        <div className="space-y-4 pt-4">
          <div className="flex items-start gap-3">
            <Checkbox 
              id="read-terms" 
              checked={hasReadTerms}
              onCheckedChange={(checked) => setHasReadTerms(checked as boolean)}
              className="mt-0.5"
            />
            <Label 
              htmlFor="read-terms" 
              className="text-sm cursor-pointer leading-relaxed peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I have read and understand the data access terms
            </Label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox 
              id="consent" 
              checked={consentGiven}
              onCheckedChange={(checked) => setConsentGiven(checked as boolean)}
              className="mt-0.5"
            />
            <Label 
              htmlFor="consent" 
              className="text-sm cursor-pointer leading-relaxed peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1 flex-wrap"
            >
              I explicitly consent to Therappi accessing my <img src={whoopLogo} alt="WHOOP" className="h-3 inline-block mx-1" /> data as described above
            </Label>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConsent}
            disabled={!hasReadTerms || !consentGiven}
            className="gap-2"
          >
            <span className="flex items-center gap-2">
              Connect <img src={whoopLogo} alt="WHOOP" className="h-4" />
            </span>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
